import "@/App.css";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import EditPage from "@/pages/EditPage";
import DetailsPage from "@/pages/DetailsPage";
import ErrorPage from "@/pages/ErrorPage";
import api, { getToken, setToken, clearToken } from "@/lib/api";
import { toast } from "sonner";

function App() {
  const [view, setView] = useState("loading"); // loading, login, landing, edit, details, error
  const [objectId, setObjectId] = useState(null);
  const [objectData, setObjectData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setView("login");
      return;
    }
    startIdFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startIdFlow = () => {
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get("id");
    if (idParam) {
      handleIdSubmit(idParam);
    } else {
      setView("landing");
    }
  };

  const handleLoginSuccess = (token) => {
    setToken(token);
    startIdFlow();
  };

  const handleLogout = () => {
    clearToken();
    setObjectId(null);
    setObjectData(null);
    setErrorMessage("");
    setView("login");
  };

  const handleIdSubmit = async (id) => {
    const fullId = `IoT:${id}`;
    setObjectId(fullId);
    setView("loading");

    try {
      const response = await api.get(`/objects/${encodeURIComponent(fullId)}/check`);
      const { exists, has_gps, data } = response.data;

      if (!exists) {
        setErrorMessage(`Objekt "${id}" wurde nicht gefunden.`);
        setView("error");
        return;
      }

      setObjectData(data);
      setView(has_gps ? "details" : "edit");
    } catch (error) {
      if (error.response?.status === 401) return; // interceptor triggers re-login
      console.error("Error checking object:", error);
      setErrorMessage("Fehler beim Laden des Objekts. Bitte versuche es erneut.");
      setView("error");
    }
  };

  const handleSaveSuccess = (updatedData) => {
    setObjectData(updatedData);
    setView("details");
    toast.success("Erfolgreich gespeichert!");
  };

  const handleEdit = () => setView("edit");

  const handleReset = () => {
    setObjectId(null);
    setObjectData(null);
    setErrorMessage("");
    setView("landing");
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  return (
    <div className="App min-h-[100dvh]">
      {view === "loading" && (
        <div className="min-h-[100dvh] flex items-center justify-center bg-[var(--sa-light)]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[var(--sa-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[var(--sa-secondary)] font-semibold">Lade Daten...</p>
          </div>
        </div>
      )}

      {view === "login" && <LoginPage onSuccess={handleLoginSuccess} />}

      {view === "landing" && (
        <LandingPage onSubmit={handleIdSubmit} onLogout={handleLogout} />
      )}

      {view === "edit" && (
        <EditPage
          objectId={objectId}
          objectData={objectData}
          onSave={handleSaveSuccess}
          onBack={handleReset}
        />
      )}

      {view === "details" && (
        <DetailsPage
          objectId={objectId}
          objectData={objectData}
          onEdit={handleEdit}
          onBack={handleReset}
        />
      )}

      {view === "error" && (
        <ErrorPage message={errorMessage} onRetry={handleReset} />
      )}

      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
